import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Search, X, ScanLine, LogOut, Loader2, Bot, RefreshCw, MessageCircle, Wallet } from "lucide-react";
import {
  API,
  FOCUS,
  entetes,
  estAnnulation,
  estNonAutorise,
  messageErreur,
  analyserRdv,
  compteARebours,
  dateCourte,
  heureRdv,
  resumeRdv,
  categorie,
  COULEUR_AVATAR,
  styleStatut,
  libelleStatut,
  capagoIncomplete,
  initiales,
  nomComplet,
  rdvBotASaisir,
} from "./outils";

// Filtres rapides (mêmes règles que le logiciel bureau, appliquées par le serveur)
const FILTRES = [
  { cle: "tous", label: "Tous", couleur: "#0F766E" },
  // 2.5.1 : réf. Capago dont le bot a trouvé un RDV aujourd'hui (compté en dossiers, remis à 0 à minuit)
  { cle: "trouve", label: "RDV trouvés aujourd\u2019hui", couleur: "#16A34A" },
  { cle: "rdv", label: "RDV pris", couleur: "#0F766E" },
  { cle: "attente", label: "En attente DZ", couleur: "#B45309" },
  { cle: "doublon", label: "Doublons", couleur: "#B91C1C" },
  { cle: "expire", label: "Comptes expirés", couleur: "#6D28D9" },
];
const DELAI_FRAPPE_MS = 300;

function useEnLigne() {
  const [enLigne, setEnLigne] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine !== false));
  useEffect(() => {
    const oui = () => setEnLigne(true);
    const non = () => setEnLigne(false);
    window.addEventListener("online", oui);
    window.addEventListener("offline", non);
    return () => {
      window.removeEventListener("online", oui);
      window.removeEventListener("offline", non);
    };
  }, []);
  return enLigne;
}

// Heure courante rafraîchie chaque minute (comptes à rebours des RDV)
export function useMaintenant(intervalle = 60000) {
  const [maintenant, setMaintenant] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setMaintenant(Date.now()), intervalle);
    return () => window.clearInterval(t);
  }, [intervalle]);
  return maintenant;
}

function LigneDossier({ groupe, personne: p, onOuvrir }) {
  const cat = categorie(p);
  const statut = libelleStatut(p, cat);
  const incomplet = capagoIncomplete(p.ref_capago);
  const detail = p.date_rdv ? resumeRdv(p.date_rdv) : p.statut_paiement;
  return (
    <li className="border-b border-[#F1F5F9] last:border-b-0" style={{ contentVisibility: "auto", containIntrinsicSize: "auto 76px" }}>
      <button
        type="button"
        onClick={() => onOuvrir(groupe, p.id)}
        data-testid="carte-groupe"
        className={`w-full min-h-[44px] flex items-center gap-3 px-3.5 py-3 text-left text-[#0F172A] bg-white hover:bg-[#F8FAFC] active:bg-[#F1F5F9] ${FOCUS} focus-visible:-outline-offset-2`}
      >
        <span
          aria-hidden="true"
          className="flex-none w-[38px] h-[38px] rounded-full text-white text-[13px] font-bold flex items-center justify-center"
          style={{ background: COULEUR_AVATAR[cat] }}
        >
          {initiales(p)}
        </span>
        <span className="flex flex-col gap-[3px] flex-1 min-w-0">
          <span className="flex items-center gap-1.5 min-w-0">
            <span className="text-[15px] font-semibold truncate" data-testid="fiche-nom">
              {nomComplet(p)}
            </span>
            {groupe.nb_membres > 1 ? (
              <span className="flex-none text-[11px] font-bold text-[#334155] bg-[#E2E8F0] rounded-md px-1.5 py-px">
                +{groupe.nb_membres - 1}
                <span className="sr-only"> autre{groupe.nb_membres > 2 ? "s" : ""} membre{groupe.nb_membres > 2 ? "s" : ""}</span>
              </span>
            ) : null}
          </span>
          {p.ref_capago ? (
            <span className="flex flex-wrap items-baseline gap-x-1.5 text-xs text-[#475569]">
              <span
                className={`font-mono ${incomplet ? "text-[#9A3412] font-bold" : "text-[#334155]"}`}
                data-testid={incomplet ? "badge-capago-incomplet" : undefined}
              >
                {p.ref_capago}
                {incomplet ? " · incomplète" : ""}
              </span>
              {p.date_capago ? <span>(attribué le {p.date_capago})</span> : null}
            </span>
          ) : null}
          {detail ? <span className="text-xs text-[#475569] truncate">{detail}</span> : null}
          {rdvBotASaisir(p) ? (
            <span
              data-testid="badge-rdv-bot"
              className="self-start inline-flex items-center gap-1 text-[11px] font-bold text-[#92400E] bg-[#FEF3C7] rounded-md px-1.5 py-0.5"
            >
              <Bot aria-hidden="true" className="w-3.5 h-3.5" />
              RDV détecté par le bot — date à saisir
            </span>
          ) : null}
        </span>
        {statut ? (
          <span
            className="flex-none max-w-[38%] truncate text-[11px] font-bold rounded-lg px-2 py-1"
            style={styleStatut(p.statut, cat)}
          >
            {statut}
          </span>
        ) : null}
      </button>
    </li>
  );
}

// 2.4.1 : bande verte défilante (comme le bot et le logiciel du bureau). Point rouge et temps restant en rouge :
// RDV à moins de 24 h. Toucher un nom ouvre la fiche (la bande s'arrête pendant l'appui).
export function BandeauRdv({ rdv72, maintenant, onOuvrirId }) {
  const cartes = useMemo(
    () =>
      (rdv72 || [])
        .map((r) => {
          const d = analyserRdv(r.date_rdv);
          if (!d) return null;
          const c = compteARebours(d, maintenant);
          if (c.passe || !c.proche) return null; // le cache serveur (60 s) peut dater d'une minute
          return { ...r, d, c };
        })
        .filter(Boolean),
    [rdv72, maintenant]
  );
  const [pause, setPause] = useState(false);
  if (!cartes.length) return null;
  // La piste contient la liste 2 fois (au moins 4 éléments par moitié) : elle défile de 50 % puis recommence.
  const fois = Math.max(1, Math.ceil(4 / cartes.length));
  const moitie = [];
  for (let k = 0; k < fois; k += 1) moitie.push(...cartes);
  const element = (r, i, cache) => {
    const h = heureRdv(r.d);
    return (
      <li key={`${cache ? "b" : "a"}-${i}-${r.id}`} className="flex-none" aria-hidden={cache || undefined}>
        <button
          type="button"
          tabIndex={cache ? -1 : 0}
          onClick={() => onOuvrirId(r.id)}
          data-testid={cache ? undefined : "bandeau-rdv-item"}
          className={`flex items-center gap-2 h-[42px] px-1 text-[13.5px] whitespace-nowrap rounded ${FOCUS}`}
        >
          <i
            className={`not-italic w-2 h-2 rounded-full flex-none ${
              r.c.urgent ? "bg-[#B91C1C] shadow-[0_0_0_3px_rgba(248,113,113,0.3)]" : "bg-[#15803D]"
            }`}
          />
          <b className="text-[#14532D] font-bold">{nomComplet(r)}</b>
          <span className="text-[#166534]">
            {dateCourte(r.d)}
            {h ? ` ${h}` : ""}
          </span>
          <span className={`font-semibold ${r.c.urgent ? "text-[#B91C1C]" : "text-[#15803D]"}`}>
            {r.c.texte.replace(/^Dans/, "dans")}
          </span>
        </button>
      </li>
    );
  };
  return (
    <section
      aria-label={`Rendez-vous sous 72 h : ${cartes.length}`}
      className="bande-rdv flex h-[42px] rounded-xl overflow-hidden bg-[#F0FDF4] border border-[#86EFAC]"
      data-testid="bandeau-rdv-proches"
      onPointerDown={() => setPause(true)}
      onPointerUp={() => setPause(false)}
      onPointerCancel={() => setPause(false)}
      onPointerLeave={() => setPause(false)}
    >
      <div className="relative z-[1] flex-none flex items-center gap-1 px-2.5 bg-[#16A34A] text-white text-[12.5px] font-extrabold whitespace-nowrap shadow-[6px_0_12px_rgba(240,253,244,0.95)]">
        <span aria-hidden="true">⏰</span> 72 H · {cartes.length}
      </div>
      <div className="bande-rdv-defile flex-1 min-w-0 overflow-hidden">
        <ul
          className={`bande-rdv-piste flex items-center gap-7 pl-4 m-0 p-0 list-none w-max ${pause ? "en-pause" : ""}`}
          style={{ animationDuration: `${Math.max(14, moitie.length * 5)}s` }}
        >
          {moitie.map((r, i) => element(r, i, i >= cartes.length))}
          {moitie.map((r, i) => element(r, i, true))}
        </ul>
      </div>
    </section>
  );
}

// 2.4 : nombre de messages WhatsApp de clients non lus (badge « Messages »), relu toutes les 60 s
function useNonLus(code, auPremierPlan) {
  const [n, setN] = useState(0);
  useEffect(() => {
    let actif = true;
    const lire = () => {
      if (document.visibilityState === "hidden") return;
      axios
        .get(`${API}/mobile/whatsapp/resume`, { headers: entetes(code), params: { auto: "1" }, timeout: 15000 })
        .then(({ data }) => {
          if (actif) setN(Number(data && data.non_lus) || 0);
        })
        .catch(() => {});
    };
    lire();
    const minuteur = window.setInterval(lire, 60000);
    document.addEventListener("visibilitychange", lire);
    return () => {
      actif = false;
      window.clearInterval(minuteur);
      document.removeEventListener("visibilitychange", lire);
    };
  }, [code, auPremierPlan]);   // relu aussi au retour sur cet écran (messages lus entre-temps)
  return n;
}

// 2.6.0 : bouton « Caisse » seulement pour les comptes autorisés par le serveur (khaldoun et Moha)
function useAccesCaisse(code) {
  const [acces, setAcces] = useState(false);
  useEffect(() => {
    let vivant = true;
    axios
      .get(`${API}/mobile/caisse/acces`, { headers: entetes(code), timeout: 15000 })
      .then((r) => vivant && setAcces(!!(r.data && r.data.acces)))
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, [code]);
  return acces;
}

export default function EcranRecherche({ code, agent, onLogout, onScanner, onMessages, onCaisse, onOuvrirFiche, auPremierPlan }) {
  const nonLus = useNonLus(code, auPremierPlan);
  const accesCaisse = useAccesCaisse(code);
  const [q, setQ] = useState("");
  const [filtre, setFiltre] = useState("tous");
  const [res, setRes] = useState(null); // dernière réponse affichée (+ q/filtre qui l'ont produite)
  const [chargement, setChargement] = useState(true);
  const [chargementPlus, setChargementPlus] = useState(false);
  const [erreur, setErreur] = useState("");
  const [essai, setEssai] = useState(0);
  const enLigne = useEnLigne();
  const maintenant = useMaintenant();

  const onLogoutRef = useRef(onLogout);
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);
  const qPrecedent = useRef(q);
  const ctrlPlus = useRef(null);

  // Recherche : délai de frappe 300 ms, annulation de la requête précédente (AbortController).
  // Une seule requête à l'ouverture. La liste précédente reste affichée pendant le chargement.
  useEffect(() => {
    const ctrl = new AbortController();
    if (ctrlPlus.current) ctrlPlus.current.abort();
    const delai = qPrecedent.current === q ? 0 : DELAI_FRAPPE_MS;
    qPrecedent.current = q;
    const minuteur = window.setTimeout(async () => {
      setChargement(true);
      setErreur("");
      try {
        const { data } = await axios.get(`${API}/mobile/search`, {
          params: { q, filtre, page: 1 },
          headers: entetes(code),
          signal: ctrl.signal,
          timeout: 30000,
        });
        setRes({ ...data, q, filtre });
      } catch (e) {
        if (estAnnulation(e)) return;
        if (estNonAutorise(e)) {
          onLogoutRef.current();
          return;
        }
        setErreur(messageErreur(e));
      } finally {
        if (!ctrl.signal.aborted) setChargement(false);
      }
    }, delai);
    return () => {
      window.clearTimeout(minuteur);
      ctrl.abort();
    };
  }, [q, filtre, code, essai]);

  useEffect(
    () => () => {
      if (ctrlPlus.current) ctrlPlus.current.abort();
    },
    []
  );

  // « Afficher 50 de plus » : page suivante AJOUTÉE à la liste (mêmes q/filtre que l'affichage)
  const chargerPlus = async () => {
    if (!res || chargementPlus || res.page >= res.pages) return;
    const ctrl = new AbortController();
    if (ctrlPlus.current) ctrlPlus.current.abort();
    ctrlPlus.current = ctrl;
    const { q: qRes, filtre: fRes, page } = res;
    setChargementPlus(true);
    setErreur("");
    try {
      const { data } = await axios.get(`${API}/mobile/search`, {
        params: { q: qRes, filtre: fRes, page: page + 1 },
        headers: entetes(code),
        signal: ctrl.signal,
        timeout: 30000,
      });
      setRes((prec) => {
        if (!prec || prec.q !== qRes || prec.filtre !== fRes || prec.page !== page) return prec;
        const vus = new Set(prec.groupes.map((g) => g.cle));
        return {
          ...prec,
          groupes: [...prec.groupes, ...(data.groupes || []).filter((g) => !vus.has(g.cle))],
          page: data.page,
          pages: data.pages,
          total_groupes: data.total_groupes,
        };
      });
    } catch (e) {
      if (estAnnulation(e)) return;
      if (estNonAutorise(e)) {
        onLogoutRef.current();
        return;
      }
      setErreur(messageErreur(e));
    } finally {
      if (ctrlPlus.current === ctrl) {
        ctrlPlus.current = null;
        setChargementPlus(false);
      }
    }
  };

  // Personne affichée par groupe : le membre concerné par le filtre et/ou qui correspond à la
  // recherche (« saidi » affiche SAIDI même s'il n'est pas le principal), sinon le principal.
  const lignes = useMemo(() => {
    if (!res) return [];
    const f = res.filtre;
    const avecQ = !!(res.q || "").trim();
    const concerne = (m) => f === "tous" || !!(m.filtres && m.filtres[f]);
    return (res.groupes || []).map((g) => {
      const m = g.membres;
      const p = (avecQ && m.find((x) => concerne(x) && x.correspond)) || m.find(concerne) || m[0];
      return { g, p };
    });
  }, [res]);

  const ouvrirParId = (id) => {
    const g = res && res.groupes.find((x) => x.membres.some((m) => m.id === id));
    onOuvrirFiche(g || null, id);
  };

  // Nouvelle recherche ou nouveau filtre affiché -> retour en haut de la liste
  // (pas lors de « Afficher 50 de plus », ni au retour d'une fiche : la liste reste montée)
  const listeRef = useRef(null);
  const cleAffichage = res ? `${res.q}\u0000${res.filtre}` : "";
  useEffect(() => {
    if (listeRef.current) listeRef.current.scrollTop = 0;
  }, [cleAffichage]);

  const compteurs = (res && res.compteurs) || {};
  const resume = res
    ? `${lignes.length} dossier${lignes.length > 1 ? "s" : ""} affiché${lignes.length > 1 ? "s" : ""} sur ${res.total_groupes}`
    : "";

  return (
    <div className="ecran flex flex-col bg-[#F4F6F9] text-[#0F172A]" data-testid="ecran-recherche">
      <header className="flex-none sticky top-0 z-10 bg-[#0F172A] px-4 pb-3.5 pt-[max(14px,env(safe-area-inset-top))] flex flex-col gap-3">
        <div className="flex items-center gap-2.5">
          <img
            src={`${process.env.PUBLIC_URL || ""}/logo.png`}
            alt=""
            aria-hidden="true"
            width="36"
            height="36"
            className="w-9 h-9 rounded-full flex-none"
          />
          <div className="flex flex-col flex-1 min-w-0">
            <h1 className="m-0 text-white font-bold text-base leading-tight">Khaldoun Office</h1>
            <span className="text-[#94A3B8] text-xs truncate" data-testid="agent-connecte">
              {agent ? `Connecté : ${agent}` : "Suivi des demandes"}
            </span>
          </div>
          <span className={`flex items-center gap-1.5 text-xs ${enLigne ? "text-[#5EEAD4]" : "text-[#FCA5A5]"}`} role="status">
            <span aria-hidden="true" className={`w-2 h-2 rounded-full ${enLigne ? "bg-[#2DD4BF]" : "bg-[#F87171]"}`} />
            {enLigne ? "En ligne" : "Hors ligne"}
          </span>
        </div>

        <div className="flex items-center gap-2 bg-[#1E293B] border border-[#334155] rounded-xl pl-3 pr-1 h-[46px] focus-within:border-[#2DD4BF]">
          <Search aria-hidden="true" className="w-[18px] h-[18px] text-[#94A3B8] flex-none" />
          <label htmlFor="recherche-q" className="sr-only">
            Rechercher
          </label>
          <input
            id="recherche-q"
            type="search"
            enterKeyHint="search"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            data-testid="input-recherche"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Nom, téléphone, passeport, Capago…"
            className="flex-1 min-w-0 bg-transparent border-0 outline-none text-white text-[15px] placeholder:text-[#94A3B8] [&::-webkit-search-cancel-button]:hidden"
          />
          {q ? (
            <button
              type="button"
              aria-label="Effacer la recherche"
              onClick={() => setQ("")}
              className={`flex-none w-11 h-11 flex items-center justify-center rounded-lg ${FOCUS}`}
            >
              <span className="w-8 h-8 rounded-lg bg-[#334155] text-[#E2E8F0] flex items-center justify-center">
                <X aria-hidden="true" className="w-3.5 h-3.5" strokeWidth={2.5} />
              </span>
            </button>
          ) : null}
        </div>

        <div className="flex gap-2 overflow-x-auto -my-[5px]" role="group" aria-label="Filtres rapides" data-testid="filtres-rapides">
          {FILTRES.map((f) => {
            const actif = filtre === f.cle;
            const n = compteurs[f.cle];
            return (
              <button
                key={f.cle}
                type="button"
                onClick={() => setFiltre(f.cle)}
                aria-pressed={actif}
                data-testid={`filtre-${f.cle}`}
                className={`flex-none h-11 flex items-center rounded-full ${FOCUS}`}
              >
                <span
                  className="h-[34px] px-3 rounded-full text-[13px] font-bold whitespace-nowrap flex items-center gap-1 border"
                  style={
                    actif
                      ? { background: f.couleur, color: "#FFFFFF", borderColor: f.couleur }
                      : f.cle === "trouve" && n > 0
                        ? { background: "rgba(22,163,74,0.14)", color: "#86EFAC", borderColor: "#16A34A" }
                        : f.cle === "trouve"
                          ? { background: "transparent", color: "#94A3B8", borderColor: "#334155" }
                          : { background: "transparent", color: "#CBD5E1", borderColor: "#475569" }
                  }
                >
                  {f.label}
                  {typeof n === "number" ? <span className="opacity-80 font-medium">{n}</span> : null}
                </span>
              </button>
            );
          })}
        </div>
      </header>

      <main
        ref={listeRef}
        className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col gap-3 p-3 [&>*]:shrink-0"
        data-testid="liste-recherche"
      >
        {res ? <BandeauRdv rdv72={res.rdv72} maintenant={maintenant} onOuvrirId={ouvrirParId} /> : null}

        <div className="flex items-center justify-between gap-2 px-1 min-h-[20px]">
          <span className="text-[13px] text-[#475569]" data-testid="compteur" aria-live="polite">
            {resume}
          </span>
          {chargement && res ? (
            <span className="flex items-center gap-1 text-xs text-[#0F766E] font-semibold" data-testid="etat-chargement">
              <Loader2 aria-hidden="true" className="w-3.5 h-3.5 animate-spin" />
              Mise à jour…
            </span>
          ) : res ? (
            <span className="text-xs text-[#0F766E] font-semibold">Résultats instantanés</span>
          ) : null}
        </div>

        {erreur ? (
          <div role="alert" className="flex items-center justify-between gap-2 bg-[#FEF2F2] border border-[#FCA5A5] text-[#B91C1C] text-sm rounded-xl px-3 py-2">
            <span>{erreur}</span>
            <button
              type="button"
              onClick={() => setEssai((n) => n + 1)}
              className={`flex-none min-h-[44px] px-3 flex items-center gap-1.5 font-semibold rounded-lg ${FOCUS}`}
            >
              <RefreshCw aria-hidden="true" className="w-4 h-4" /> Réessayer
            </button>
          </div>
        ) : null}

        <div className="bg-white border border-[#E2E8F0] rounded-[14px] overflow-hidden">
          {!res ? (
            <p className="m-0 p-7 text-center text-[#475569] text-sm flex items-center justify-center gap-2">
              {chargement ? (
                <>
                  <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" /> Chargement…
                </>
              ) : (
                "Aucune donnée"
              )}
            </p>
          ) : lignes.length === 0 ? (
            <p className="m-0 p-7 text-center text-[#475569] text-sm" data-testid="etat-vide">
              Aucune demande trouvée
            </p>
          ) : (
            <ul className="list-none m-0 p-0">
              {lignes.map(({ g, p }) => (
                <LigneDossier key={g.cle} groupe={g} personne={p} onOuvrir={onOuvrirFiche} />
              ))}
            </ul>
          )}
        </div>

        {res && res.page < res.pages ? (
          <button
            type="button"
            onClick={chargerPlus}
            disabled={chargementPlus}
            data-testid="btn-plus"
            className={`flex-none h-11 rounded-xl border border-[#CBD5E1] bg-white text-[#334155] text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 ${FOCUS}`}
          >
            {chargementPlus ? (
              <>
                <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" /> Chargement…
              </>
            ) : (
              `Afficher ${Math.min(50, res.total_groupes - lignes.length)} de plus`
            )}
          </button>
        ) : null}
      </main>

      <nav
        aria-label="Navigation"
        className="flex-none flex bg-white border-t border-[#E2E8F0] px-2 pt-1.5 pb-[max(10px,env(safe-area-inset-bottom))]"
      >
        <button
          type="button"
          aria-current="page"
          onClick={() => listeRef.current && listeRef.current.scrollTo({ top: 0, behavior: "smooth" })}
          className={`flex-1 min-h-[44px] flex flex-col items-center gap-0.5 py-1.5 text-xs font-bold text-[#0F766E] rounded-lg ${FOCUS}`}
        >
          <Search aria-hidden="true" className="w-[22px] h-[22px]" />
          Recherche
        </button>
        <button
          type="button"
          onClick={onScanner}
          data-testid="btn-ouvrir-scanner"
          className={`flex-1 min-h-[44px] flex flex-col items-center gap-0.5 py-1.5 text-xs font-semibold text-[#475569] rounded-lg ${FOCUS}`}
        >
          <ScanLine aria-hidden="true" className="w-[22px] h-[22px]" />
          Scanner
        </button>
        <button
          type="button"
          onClick={onMessages}
          data-testid="btn-messages"
          aria-label={nonLus ? `Messages, ${nonLus} non lu${nonLus > 1 ? "s" : ""}` : "Messages"}
          className={`relative flex-1 min-h-[44px] flex flex-col items-center gap-0.5 py-1.5 text-xs font-semibold text-[#475569] rounded-lg ${FOCUS}`}
        >
          <MessageCircle aria-hidden="true" className="w-[22px] h-[22px]" />
          Messages
          {nonLus ? (
            <span
              aria-hidden="true"
              className="absolute top-0.5 left-1/2 ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[11px] font-bold flex items-center justify-center"
            >
              {nonLus > 99 ? "99+" : nonLus}
            </span>
          ) : null}
        </button>
        {accesCaisse && onCaisse ? (
          <button
            type="button"
            onClick={onCaisse}
            data-testid="btn-caisse"
            className={`flex-1 min-h-[44px] flex flex-col items-center gap-0.5 py-1.5 text-xs font-semibold text-[#475569] rounded-lg ${FOCUS}`}
          >
            <Wallet aria-hidden="true" className="w-[22px] h-[22px]" />
            Caisse
          </button>
        ) : null}
        <button
          type="button"
          onClick={onLogout}
          data-testid="btn-logout"
          className={`flex-1 min-h-[44px] flex flex-col items-center gap-0.5 py-1.5 text-xs font-semibold text-[#475569] rounded-lg ${FOCUS}`}
        >
          <LogOut aria-hidden="true" className="w-[22px] h-[22px]" />
          Déconnexion
        </button>
      </nav>
    </div>
  );
}
